import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, renderSlot as _renderSlot, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives, mergeProps as _mergeProps, withCtx as _withCtx, createSlots as _createSlots, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-efac42be"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "select__wrapper" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, null, -1))
const _hoisted_3 = { class: "select__no-results" }
const _hoisted_4 = { class: "select__option-wrapper" }
const _hoisted_5 = ["onMouseenter"]
const _hoisted_6 = { class: "select__option-label" }

import VueMultiselect from 'vue-multiselect'
import BaseIcon from '@/components/BaseIcon.vue'
import { computed, ref } from 'vue'

/*
This component is using "vue-multiselect".
Documentation for examples, props, events and slots: https://vue-multiselect.js.org
*/


import { PropType } from 'vue'
import { Option } from '@/models/shared.model'

export type BaseSelectModelValue = object[] | object | string | number | null

export type SelectOptionTypes = Option | string | number

export type CustomLabelFuncType = (option: SelectOptionTypes) => string

export const propsModelValue = {
  type: [Array, Object, String, Number, null] as PropType<BaseSelectModelValue>,
  required: true,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'BaseSelect',
  props: {
  modelValue: propsModelValue,
  options: {
    type: Array as PropType<Array<Option> | Array<string> | Array<number>>,
    required: true,
  },
  returnObject: {
    type: Boolean,
    default: false,
  },
  // Label from option Object, that will be visible in the dropdown.
  optionLabel: {
    type: String,
    default: 'label',
  },
  // Used to compare objects. Only use if options are objects.
  trackBy: {
    type: String,
    default: 'key',
  },
  errors: {
    type: Array as PropType<string[] | undefined>,
    default: () => [],
  },
  // Equivalent to the multiple attribute on a <select> input.
  multiple: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  // Equivalent to the placeholder attribute on a <select> input.
  // If undefined it will fallback to label prop
  placeholder: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  // Show tags in select as chip
  showTags: {
    type: Boolean,
    default: false,
  },
  // Show the noResult slot if no results are found.
  noSearchResultText: {
    type: String,
    default: undefined,
  },
  // Show the showNoOptions slot if list is empty.
  noOptionsText: {
    type: String,
    default: undefined,
  },
  // Decide whether to show labels on highlighted options
  showLabels: {
    type: Boolean,
    default: false,
  },
  // Enable/disable closing after selecting an option
  closeOnSelect: {
    type: Boolean,
    default: false,
  },
  // Enable/disable highlighting of the pointed value.
  showPointer: {
    type: Boolean,
    default: true,
  },
  // Allows to remove all selected values. Otherwise one must be left selected.
  // Todo: Known bug: if multiple is true and allowEmpty is false then delete icon is not shown
  allowEmpty: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  customLabel: {
    type: Function,
    default: undefined,
  },
  customOptionClass: {
    type: Function as PropType<
      (
        option: Option | string | number,
      ) => string | Array<string> | Record<string, string>
    >,
    default: undefined,
  },
  customMultiselectLabel: {
    type: String,
    default: undefined,
  },
  openDirection: {
    type: String,
    default: '',
  },
  taggable: {
    type: Boolean,
    default: false,
  },
},
  emits: ['select', 'update:modelValue', 'addNewOption'],
  setup(__props, { emit }) {

const props = __props



const selectRef = ref()
const isOpen = ref(false)
const hoveredOption = ref(null)

function updateModelValue(value: number | object) {
  if (!props.returnObject) {
    value = getValue(value)
  }

  emit('select', value)
  if (props.multiple && !props.returnObject) {
    emit(
      'update:modelValue',
      props.multiple
        ? [
            ...(props.modelValue as Option[]).map((option) => getValue(option)),
            value,
          ]
        : value,
    )
  } else {
    emit(
      'update:modelValue',
      props.multiple ? [...(props.modelValue as Option[]), value] : value,
    )
  }
}

/**
 * Check first option to make sure whether we are dealing with object options or not.
 * That is important because if options are not object we should empty trackBy and label props of VueMultiSelect.
 *
 * We expect all the options to be the same data type.
 */
const isObjectOptions = computed<boolean>(() => {
  const firstOption = props.options?.[0]
  return typeof firstOption === 'object'
})

const isSelected = computed(() => {
  let valueToCheck
  if (Array.isArray(props.modelValue)) {
    valueToCheck = getValue(props.modelValue[0])
  } else {
    valueToCheck = getValue(props.modelValue)
  }
  return selectRef.value?.valueKeys?.indexOf(valueToCheck) >= 0
})

function isOptionSelected(option: Option | number | string) {
  return !!selectRef.value?.isSelected(option)
}

function getValue(option: Option | object | number | string | null) {
  return option?.[props.trackBy] ?? option
}

function getLabel(option: Option | number | string) {
  // Use VueMultiSelect inner method instead of trying to recreate ourselves.
  return selectRef.value?.getOptionLabel(option)
}

const addTag = (newTag: string) => {
  emit('addNewOption', newTag)
}



return (_ctx: any,_cache: any) => {
  const _component_RequiredAsterisk = _resolveComponent("RequiredAsterisk")!
  const _component_InputErrorMessages = _resolveComponent("InputErrorMessages")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isSelected) || __props.required)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: _normalizeClass(["select__label", { 'select__label--active': isOpen.value }])
        }, [
          _createTextVNode(_toDisplayString(__props.label), 1),
          (__props.required)
            ? (_openBlock(), _createBlock(_component_RequiredAsterisk, { key: 0 }))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    _createVNode(_unref(VueMultiselect), _mergeProps({
      ref_key: "selectRef",
      ref: selectRef,
      class: "select__field",
      "model-value": __props.modelValue,
      options: __props.options,
      placeholder: __props.placeholder ?? __props.label,
      multiple: __props.multiple,
      label: _unref(isObjectOptions) ? __props.optionLabel : '',
      "track-by": _unref(isObjectOptions) ? __props.trackBy : '',
      "show-labels": __props.showLabels,
      "close-on-select": __props.closeOnSelect,
      "show-pointer": __props.showPointer,
      "allow-empty": __props.allowEmpty,
      disabled: __props.disabled,
      "custom-label": __props.customLabel
    }, _ctx.$attrs, {
      "open-direction": __props.openDirection,
      taggable: __props.taggable,
      onTag: addTag,
      onOpen: _cache[1] || (_cache[1] = ($event: any) => (isOpen.value = true)),
      onClose: _cache[2] || (_cache[2] = ($event: any) => (isOpen.value = false)),
      onSelect: _cache[3] || (_cache[3] = 
        (value) => {
          updateModelValue(value)
        }
      )
    }), _createSlots({
      noResult: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, _toDisplayString(__props.noSearchResultText || _ctx.$t('noSearchResults')), 1)
      ]),
      noOptions: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, _toDisplayString(__props.noOptionsText || _ctx.$t('noOptions')), 1)
      ]),
      singleLabel: _withCtx(({ option }) => [
        _renderSlot(_ctx.$slots, "selectedLabel", { option: option })
      ]),
      beforeList: _withCtx(() => [
        _renderSlot(_ctx.$slots, "beforeList")
      ]),
      option: _withCtx(({ option }) => [
        _createElementVNode("div", {
          class: _normalizeClass(["select__option-wrapper", __props.customOptionClass?.(option)]),
          onMouseenter: ($event: any) => (hoveredOption.value = getValue(option)),
          onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (hoveredOption.value = null))
        }, [
          _createElementVNode("div", _hoisted_6, [
            _renderSlot(_ctx.$slots, "optionPrepend", { option: option }),
            _createTextVNode(" " + _toDisplayString(getLabel(option)) + " ", 1),
            _renderSlot(_ctx.$slots, "optionAppend", { option: option })
          ]),
          (isOptionSelected(option))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (hoveredOption.value === getValue(option) && __props.allowEmpty)
                  ? (_openBlock(), _createBlock(BaseIcon, {
                      key: 0,
                      class: "select__option-icon-delete",
                      icon: "X",
                      size: 16
                    }))
                  : (_openBlock(), _createBlock(BaseIcon, {
                      key: 1,
                      class: "select__option-icon-selected",
                      icon: "check",
                      size: 16
                    }))
              ], 64))
            : _createCommentVNode("", true)
        ], 42, _hoisted_5)
      ]),
      selection: _withCtx(({ values }) => [
        (values.length && __props.customMultiselectLabel)
          ? _withDirectives((_openBlock(), _createElementBlock("span", {
              key: 0,
              class: "multiselect__single"
            }, _toDisplayString(__props.customMultiselectLabel), 513)), [
              [_vShow, !isOpen.value]
            ])
          : (values.length)
            ? _withDirectives((_openBlock(), _createElementBlock("span", {
                key: 1,
                class: "multiselect__single"
              }, _toDisplayString(_ctx.$t('optionSelectedLabel', { length: values.length })), 513)), [
                [_vShow, !isOpen.value]
              ])
            : _createCommentVNode("", true)
      ]),
      _: 2
    }, [
      (!__props.showTags)
        ? {
            name: "tag",
            fn: _withCtx(() => [
              _hoisted_2
            ]),
            key: "0"
          }
        : undefined
    ]), 1040, ["model-value", "options", "placeholder", "multiple", "label", "track-by", "show-labels", "close-on-select", "show-pointer", "allow-empty", "disabled", "custom-label", "open-direction", "taggable"]),
    _createVNode(_component_InputErrorMessages, { "model-value": __props.errors }, null, 8, ["model-value"])
  ]))
}
}

})